/**
 * Which definitions a report's cases are about.
 *
 * A case is an equation, and an equation names definitions. The names a case
 * mentions that the level also asks for are exactly the definitions that case
 * puts under test, so grouping the cases by that set tells the player which of
 * their definitions a wall of results is actually about. The grouping is a
 * presentation of fact, not a guess: a case that mentions `fst` and `pair`
 * appears under both.
 *
 * Groups keep the order in which their first case appears, so a level's cases
 * read in the order they were written.
 */

import type { CaseSpec, Level } from '@spec';

/** Every source a case compares, in the fixed order the engine sees them. */
export function caseSources(entry: Pick<CaseSpec, 'lhs' | 'rhs' | 'also'>): string[] {
  return [entry.lhs, entry.rhs, ...(entry.also ?? [])];
}

export interface CaseGroup {
  /** The level's own names this group's cases exercise, in the level's order. */
  names: string[];
  /** Positions of the group's cases in the level's case list. */
  indices: number[];
}

/**
 * Groups cases by the names they exercise.
 *
 * `perCase` holds, for each case, the level's own names that the case mentions,
 * in the level's slot order. A uniform input gives one group with no names,
 * which means the report is not worth splitting; no cases give no groups.
 */
export function groupBySlot(perCase: readonly (readonly string[])[]): CaseGroup[] {
  const groups: CaseGroup[] = [];
  const byKey = new Map<string, CaseGroup>();

  perCase.forEach((names, index) => {
    const key = names.join('\u0000');
    const existing = byKey.get(key);
    if (existing !== undefined) {
      existing.indices.push(index);
      return;
    }
    const group: CaseGroup = { names: [...names], indices: [index] };
    byKey.set(key, group);
    groups.push(group);
  });

  return groups;
}

/** What the fixed case sources mention, worked out once per session. */
export interface CaseData {
  /** Names each level's cases mention, for the context pane. */
  mentions: Map<string, string[]>;
  /** The level's own names each case exercises, in the level's slot order. */
  slots: Map<string, string[][]>;
}

/**
 * Works out what the fixed case sources mention.
 *
 * A case is a set of sources, so the level's own names that a case exercises
 * are the free names of all of them intersected with the level's slots. Those
 * are exactly the definitions a failure in that case could be about.
 */
export function analyseCases(levels: readonly Level[], references: readonly string[][]): CaseData {
  const mentions = new Map<string, string[]>();
  const slots = new Map<string, string[][]>();
  let cursor = 0;
  for (const level of levels) {
    const names = new Set<string>();
    const perCase: string[][] = [];
    for (const entry of level.cases) {
      const sources = caseSources(entry);
      const mentioned = new Set<string>();
      for (let index = 0; index < sources.length; index += 1) {
        for (const name of references[cursor + index] ?? []) {
          mentioned.add(name);
        }
      }
      cursor += sources.length;
      for (const name of mentioned) {
        names.add(name);
      }
      perCase.push(level.slots.filter((slot) => mentioned.has(slot.name)).map((slot) => slot.name));
    }
    mentions.set(level.id, [...names]);
    slots.set(level.id, perCase);
  }
  return { mentions, slots };
}
